import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';
import { HotWheel, HotWheelUpdateInput } from '../../types/hotwheels.types';
import {
  HotWheelFormFields,
  HotWheelFormValues,
} from '../hotwheel-form-fields/hotwheel-form-fields';
import { HotWheelImageUploader } from '../hotwheel-image-uploader/hotwheel-image-uploader';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { useUpdateHotWheel, useGetHotWheel } from '../../hooks/use-hotwheels';

/**
 * EditHotwheelModal — opens when a card is clicked.
 *
 * Compose:
 *   - Image uploader (top)
 *   - Form fields (middle)
 *   - Flags (favorite / treasure hunt / super / limited / public)
 *   - Save / Cancel
 *
 * Saves via useUpdateHotWheel; closes on success.
 */

interface EditHotwheelModalProps {
  car: HotWheel | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditHotwheelModal({ car, open, onOpenChange }: EditHotwheelModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync: updateCar, isPending } = useUpdateHotWheel();
  const [mainImageId, setMainImageId] = useState<string | null>(null);
  const [flags, setFlags] = useState({
    Favorite: false,
    TreasureHunt: false,
    SuperTreasureHunt: false,
    LimitedEdition: false,
    PublicVisibility: false,
  });

  useEffect(() => {
    if (car) {
      setMainImageId(car.MainImage ?? null);
      setFlags({
        Favorite: car.Favorite,
        TreasureHunt: car.TreasureHunt,
        SuperTreasureHunt: car.SuperTreasureHunt,
        LimitedEdition: car.LimitedEdition,
        PublicVisibility: car.PublicVisibility,
      });
    }
  }, [car]);

  if (!car) return null;

  const defaultValues: Partial<HotWheelFormValues> = {
    Brand: car.Brand,
    Series: car.Series,
    ModelNumber: car.ModelNumber,
    ReleaseYear: car.ReleaseYear,
    Color: car.Color ?? '',
    Scale: car.Scale ?? '',
    Condition: car.Condition as HotWheelFormValues['Condition'],
    PurchasePrice: car.PurchasePrice as unknown as HotWheelFormValues['PurchasePrice'],
    EstimatedValue: car.EstimatedValue as unknown as HotWheelFormValues['EstimatedValue'],
    PurchaseDate: car.PurchaseDate?.split('T')[0] ?? '',
    StorageLocation: car.StorageLocation ?? '',
    Description: car.Description ?? '',
  };

  const handleSubmit = async (values: HotWheelFormValues) => {
    try {
      const payload: HotWheelUpdateInput = {
        ...values,
        ...flags,
        MainImage: mainImageId ?? undefined,
        PurchasePrice: values.PurchasePrice === '' || values.PurchasePrice === undefined ? undefined : Number(values.PurchasePrice),
        EstimatedValue: values.EstimatedValue === '' || values.EstimatedValue === undefined ? undefined : Number(values.EstimatedValue),
      };
      await updateCar({ itemId: car.ItemId, input: payload });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t('SAVE_FAILED'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="text-white">{t('EDIT_CAR')}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {car.Brand} · {car.ModelNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <HotWheelImageUploader
              value={mainImageId}
              onChange={(fileId) => setMainImageId(fileId)}
            />
          </div>

          <div className="space-y-4">
            <HotWheelFormFields
              defaultValues={defaultValues}
              onSubmit={handleSubmit}
              isPending={isPending}
              submitLabel={t('SAVE')}
              onCancel={() => onOpenChange(false)}
            />
            <div className="space-y-2 border-t border-zinc-800 pt-4">
              {(
                [
                  ['Favorite', t('FAVORITE')],
                  ['TreasureHunt', t('TREASURE_HUNT')],
                  ['SuperTreasureHunt', t('SUPER_TREASURE_HUNT')],
                  ['LimitedEdition', t('LIMITED_EDITION')],
                  ['PublicVisibility', t('PUBLIC_VISIBILITY')],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={flags[key as keyof typeof flags]}
                    onCheckedChange={(checked) =>
                      setFlags((f) => ({ ...f, [key]: Boolean(checked) }))
                    }
                  />
                  <span className="text-sm text-zinc-200">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}